/**
 * Cetak CPI banyak sumur — satu berkas, satu halaman A3 per sumur.
 *
 * Lembarnya digambar oleh `CpiSheet`, komponen yang sama persis dengan yang
 * dipakai jalur satu sumur. Itu bukan penghematan baris: sebuah berkas cetak
 * berisi dua puluh sumur yang halamannya berbeda tipis dari halaman yang
 * dicetak sendiri-sendiri adalah dua tafsiran yang dipisahkan hanya oleh cara
 * memintanya, dan selisihnya baru ketahuan setelah kertasnya masuk arsip.
 *
 * Sumur yang dipilih tinggal di URL, bukan di state komponen. Sebuah berkas
 * cetak adalah benda yang dikirimkan — "cetak lembar ini" hampir selalu
 * berarti menempelkan tautannya kepada orang lain — dan pilihan yang hanya
 * hidup di dalam tab tidak bisa ditempelkan ke mana pun.
 */

import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { api, unpage, type Page } from "../api";
import { Button, Checkbox, Loading, Notice } from "../components/ui";
import { useAsync } from "../hooks";
import type { CpiPayload, Well } from "../types";
import { CpiSheet } from "./PrintCPI";

interface MultiCpiPayload {
  project: string;
  count: number;
  sheets: CpiPayload[];
  /** Sumur yang tidak bisa dicetak, berikut alasannya masing-masing. */
  skipped: { well: string; reason: string }[];
}

export default function PrintCPIMulti() {
  const { id } = useParams();
  const [search, setSearch] = useSearchParams();
  const wanted = (search.get("wells") ?? "").split(",").filter(Boolean);

  const document = useAsync(
    (signal) =>
      wanted.length === 0
        ? Promise.resolve(null)
        : api.get<MultiCpiPayload>(
            "/api/exports/cpi/",
            {
              wells: wanted.join(","),
              top: search.get("top") ?? undefined,
              base: search.get("base") ?? undefined,
              scale: search.get("scale") ?? 200,
            },
            signal,
          ),
    [search.toString()],
  );

  if (wanted.length === 0) {
    return <WellPicker projectId={id} onChoose={(ids) => {
      const next = new URLSearchParams(search);
      next.set("wells", ids.join(","));
      setSearch(next);
    }} />;
  }

  if (document.loading) return <Loading label="Laying out the CPI sheet…" />;
  if (document.error)
    return (
      <div style={{ padding: 24 }}>
        <Notice tone="error">{document.error}</Notice>
      </div>
    );
  if (!document.data) return null;

  const data = document.data;

  return (
    <div className="print-sheets" style={{ minHeight: "100%", background: "var(--app-bg)", padding: "20px 0 60px" }}>
      <div
        className="no-print"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          maxWidth: 1587,
          margin: "0 auto 16px",
          padding: "0 8px",
          flexWrap: "wrap",
        }}
      >
        <span style={{ font: "600 13px var(--font-sans)", color: "var(--ink)" }}>
          Pratinjau cetak — {data.count} lembar A3 mendatar
        </span>
        <span style={{ font: "400 11px var(--font-mono)", color: "var(--ink-6)" }}>
          {data.project}
        </span>
        <span style={{ flex: 1 }} />
        <Button
          variant="ghost"
          onClick={() => {
            // Kembali ke pemilihnya, bukan ke riwayat browser: yang hampir
            // selalu dituju sesudah melihat pratinjau adalah menambah atau
            // mengurangi satu sumur, dan riwayat akan melempar ke layar lain.
            const next = new URLSearchParams(search);
            next.delete("wells");
            setSearch(next);
          }}
        >
          Change the selection
        </Button>
        <Button variant="primary" onClick={() => window.print()}>
          Print / save PDF
        </Button>
      </div>

      {/* Sumur yang tidak bisa dicetak disebut namanya, di layar saja. Yang
          memintanya perlu tahu bahwa daftarnya tidak utuh — tetapi kertasnya
          adalah tafsiran, bukan catatan tentang apa yang gagal disusun. */}
      {data.skipped.length > 0 && (
        <div className="no-print" style={{ maxWidth: 1587, margin: "0 auto 16px", padding: "0 8px" }}>
          <Notice>
            {`${data.skipped.length} wells not printed — `}
            {data.skipped.map((row) => `${row.well}: ${row.reason}`).join(" · ")}
          </Notice>
        </div>
      )}

      <div className="print-sheets" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {data.sheets.map((sheet, index) => (
          <CpiSheet
            key={sheet.well_id ?? index}
            data={sheet}
            // Halaman terakhir tidak diikuti pemutus halaman; kalau diikuti,
            // berkasnya berakhir dengan satu lembar kosong.
            breakAfter={index < data.sheets.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Pemilih sumur, muncul selama URL-nya belum menyebutkan satu pun.
 *
 * Urutan centangnya adalah urutan cetaknya. Sebuah berkas cetak dibaca dari
 * depan ke belakang dan urutannya membawa keterangan — dari barat ke timur,
 * dari tua ke muda — jadi yang dicentang lebih dulu berdiri lebih dulu, bukan
 * yang nomornya lebih kecil.
 */
function WellPicker({
  projectId,
  onChoose,
}: {
  projectId: string | undefined;
  onChoose: (ids: number[]) => void;
}) {
  const wells = useAsync(
    (signal) =>
      api
        .get<Page<Well>>("/api/wells/", { project: projectId }, signal)
        .then((page) => unpage(page, signal)),
    [projectId],
  );
  const [picked, setPicked] = useState<number[]>([]);

  /*
    Tidak ada yang disaring di sini. Sumur rencana memang tidak akan punya
    lembar CPI — itu keputusan yang berdiri di server dan tidak berubah:
    mencetak net pay atas lubang yang belum dibor adalah laporan volumetrik
    yang kertasnya sendiri tidak mengatakan demikian. Yang keliru adalah
    menyembunyikan namanya, sebab nama yang tidak muncul tidak mengatakan
    mengapa ia tidak muncul: yang mencarinya tidak tahu apakah sumurnya hilang
    karena statusnya, karena kurvanya, atau karena ia tidak ada di proyek ini
    sama sekali. Ditawarkan apa adanya, dan `skipped` menyebutkan sebabnya per
    sumur sesudah lembarnya benar-benar dicoba disusun.
  */
  const offered = wells.data ?? [];

  const toggle = (id: number) =>
    setPicked((current) =>
      current.includes(id) ? current.filter((one) => one !== id) : [...current, id],
    );

  return (
    <div style={{ padding: "24px 0 60px", background: "var(--app-bg)", minHeight: "100%" }}>
      <div style={{ maxWidth: 640, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 }}>
        <span style={{ font: "600 14px var(--font-sans)", color: "var(--ink)" }}>
          Print CPI — pick the wells
        </span>
        <span style={{ font: "400 11.5px/1.6 var(--font-sans)", color: "var(--ink-5)" }}>
          One A3 landscape page per well, in a single file. The order they are ticked is
          the order they print. A well that yields no sheet — a planned well, say, because
          a CPI sheet is a report on a hole that has been drilled — is named together with
          its reason once the sheets are laid out.
        </span>

        {wells.loading && <Loading />}
        {wells.error && <Notice tone="error">{wells.error}</Notice>}
        {!wells.loading && offered.length === 0 && (
          <Notice>This project has no well yet.</Notice>
        )}

        <div
          className="scroll-y"
          style={{
            maxHeight: 420,
            border: "1px solid var(--border-control)",
            borderRadius: "var(--radius-panel)",
            background: "var(--panel)",
            padding: "8px 12px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {offered.map((well) => {
            const order = picked.indexOf(well.id);
            return (
              <div key={well.id} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <Checkbox
                  checked={order >= 0}
                  onChange={() => toggle(well.id)}
                  label={well.name}
                />
                <span style={{ flex: 1 }} />
                <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-6)" }}>
                  {order >= 0 ? `#${order + 1}` : ""}
                </span>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Button onClick={() => setPicked(offered.map((well) => well.id))}>
            Select all
          </Button>
          <Button variant="ghost" disabled={picked.length === 0} onClick={() => setPicked([])}>
            Clear
          </Button>
          <span style={{ flex: 1 }} />
          <span style={{ font: "400 11px var(--font-mono)", color: "var(--ink-6)" }}>
            {picked.length} terpilih
          </span>
          <Button variant="primary" disabled={picked.length === 0} onClick={() => onChoose(picked)}>
            Susun {picked.length} lembar
          </Button>
        </div>
      </div>
    </div>
  );
}
